'use client';

import React from 'react';
import { Box } from '@mui/material';
import { ExpandMoreIcon } from '@/components/icons';
import { findCountryByIso } from '../countries';
import { IntakeCountryPickerMenu } from './IntakeCountryPickerMenu';
import type { CountryPickerMode } from './IntakeCountryPickerList';

export type IntakeCountryPickerProps = {
  mode: CountryPickerMode;
  id: string;
  listId: string;
  value: string;
  detectedIso: string | null;
  placeholder: string;
  searchPlaceholder: string;
  searchAriaLabel: string;
  listAriaLabel: string;
  /** Accessible name when there is no visible label id. */
  ariaLabel?: string;
  /** Prefer associating a visible label via id. */
  ariaLabelledBy?: string;
  /** Full accessible name; replaces the label/value composition. */
  accessibleName?: string;
  required?: boolean;
  error?: string;
  errorId?: string;
  fullWidth?: boolean;
  onChange: (iso2: string) => void;
  onBlur: () => void;
};

export function IntakeCountryPicker({
  mode,
  id,
  listId,
  value,
  detectedIso,
  placeholder,
  searchPlaceholder,
  searchAriaLabel,
  listAriaLabel,
  ariaLabel,
  ariaLabelledBy,
  accessibleName,
  required = false,
  error,
  errorId,
  fullWidth = false,
  onChange,
  onBlur,
}: IntakeCountryPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const selected = findCountryByIso(value);
  const label =
    mode === 'dial'
      ? selected
        ? `${selected.iso2} ${selected.dialCode}`
        : placeholder
      : selected
        ? selected.name
        : placeholder;
  const valueId = `${id}-value`;
  const labelledBy =
    ariaLabelledBy && !accessibleName ? `${ariaLabelledBy} ${valueId}` : undefined;
  const accessibleLabel =
    accessibleName ??
    (ariaLabelledBy ? undefined : ariaLabel ? `${ariaLabel}, ${label}` : undefined);

  const close = React.useCallback(
    (restoreFocus = true) => {
      setOpen(false);
      onBlur();
      if (restoreFocus) {
        buttonRef.current?.focus();
      }
    },
    [onBlur]
  );

  function select(iso2: string) {
    onChange(iso2);
    setOpen(false);
    onBlur();
    buttonRef.current?.focus();
  }

  function openMenu() {
    setOpen(true);
  }

  function onTriggerKeyDown(event: React.KeyboardEvent) {
    // Enter/Space already activate the button (click). Only ArrowDown needs a hand.
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) openMenu();
    }
  }

  return (
    <Box
      sx={{
        flex: fullWidth ? '1 1 auto' : '0 0 auto',
        width: fullWidth ? '100%' : 'auto',
        position: 'relative',
      }}
    >
      <Box
        component="button"
        type="button"
        id={id}
        ref={(node: HTMLButtonElement | null) => {
          buttonRef.current = node;
          setAnchor(node);
        }}
        aria-label={accessibleLabel}
        aria-labelledby={labelledBy}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        aria-required={required || undefined}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={error && errorId ? errorId : undefined}
        onClick={() => {
          if (open) {
            close(true);
          } else {
            openMenu();
          }
        }}
        onKeyDown={onTriggerKeyDown}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          width: fullWidth ? '100%' : 'auto',
          height: 40,
          px: 1.25,
          border: '1px solid',
          borderColor: error ? 'error.main' : open ? 'primary.main' : 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          color: selected ? 'text.primary' : 'text.secondary',
          font: 'inherit',
          fontSize: 14,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          boxShadow: open ? theme => `0 0 0 1px ${theme.palette.primary.main}` : 'none',
          outline: 'none',
          '&:focus-visible': {
            boxShadow: theme => `0 0 0 2px ${theme.palette.primary.main}`,
            borderColor: 'primary.main',
          },
        }}
      >
        <Box
          component="span"
          id={valueId}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
          }}
        >
          {label}
        </Box>
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: 'text.secondary',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </Box>
      {open && anchor && (
        <IntakeCountryPickerMenu
          anchor={anchor}
          mode={mode}
          listId={listId}
          value={value}
          detectedIso={detectedIso}
          searchPlaceholder={searchPlaceholder}
          searchAriaLabel={searchAriaLabel}
          listAriaLabel={listAriaLabel}
          onSelect={select}
          onClose={close}
        />
      )}
    </Box>
  );
}
